import { useState } from 'react'

import { fehlertext } from '../../api/client'
import type { EnvEintrag } from '../../api/types'
import { rueckfrage } from '../../components/Rueckfrage'
import { useEnv, useEnvUndo, useMe, useSaveEnv } from '../../hooks/queries'
import { useTexte } from '../../texte'

/**
 * Die Werte der `.env`, ein Abschnitt je Karte, eine Zeile je Schlüssel (#62).
 *
 * **Kein Texteditor über der Datei.** Jede Zeile kennt ihre Art (Zahl,
 * Wahrheitswert, Auswahl, Adresse) und ihre Stufe: frei, geprüft, nur lesen.
 * Der Server entscheidet alles davon (`EnvCatalogue`); hier wird nur das
 * passende Feld gezeigt und die Antwort des Servers als Text.
 *
 * **Geheimnisse kommen nie als Wert.** Die Zeile sagt „gesetzt" oder „leer",
 * und ein leeres Feld nimmt einen neuen Wert entgegen.
 *
 * Gespeichert wird je Zeile: eine Änderung ist eine Änderung der Datei, und
 * ein Entwurf über zwanzig Felder, der am Ende an einem scheitert, wäre die
 * schlechtere Bedienung.
 */
export function EnvCard() {
  const t = useTexte().betrieb
  const me = useMe()
  const env = useEnv(me.data?.may_operate === true)
  const undo = useEnvUndo()
  const [zurueckgenommen, setZurueckgenommen] = useState(false)

  if (env.isPending) return <p className="card__lead leise">…</p>
  if (env.isError) {
    return (
      <p className="notice" role="alert">
        {fehlertext(env.error)}
      </p>
    )
  }
  const seite = env.data

  return (
    <>
      <div className="card card--seite">
        <p className="card__lead card__lead--dicht">{t.konfigurationLead}</p>
        {!seite.exists && (
          <p className="notice" role="alert" data-env-stand="fehlt">
            {t.fehlt}
          </p>
        )}
        {seite.exists && !seite.writable && (
          <p className="notice" role="alert" data-env-stand="nur-lesen">
            {t.nichtSchreibbar}
          </p>
        )}
        {seite.undo_available && (
          <div className="row row--eng row--oben">
            <button
              type="button"
              className="button button--outline button--small"
              disabled={undo.isPending}
              data-env-undo=""
              onClick={() =>
                undo.mutate(undefined, {
                  onSuccess: () => setZurueckgenommen(true),
                })
              }
            >
              {t.zurueckNehmen}
            </button>
          </div>
        )}
        {zurueckgenommen && !seite.undo_available && (
          <p className="feld__hinweis feld__hinweis--13" role="status">
            {t.zurueckgenommen}
          </p>
        )}
        {undo.isError && (
          <p className="notice" role="alert">
            {fehlertext(undo.error)}
          </p>
        )}
        {seite.unknown.length > 0 && (
          <p className="feld__hinweis feld__hinweis--13 feld__hinweis--abstand" data-env-unbekannt="">
            {t.unbekannt} <code>{seite.unknown.join(', ')}</code>
          </p>
        )}
      </div>

      {seite.sections.map((abschnitt) => (
        <div className="card card--seite" key={abschnitt.key} data-env-abschnitt={abschnitt.key}>
          <h2 className="card__titel">{abschnitt.title}</h2>
          <div className="stack stack--eng">
            {abschnitt.entries.map((eintrag) => (
              <EnvZeile key={eintrag.key} eintrag={eintrag} schreibbar={seite.writable} />
            ))}
          </div>
        </div>
      ))}
    </>
  )
}

/** Eine Zeile: Name, Satz, Feld oder Wert, und was der Server dazu sagt. */
function EnvZeile({ eintrag, schreibbar }: { eintrag: EnvEintrag; schreibbar: boolean }) {
  const t = useTexte().betrieb
  const save = useSaveEnv()
  const [entwurf, setEntwurf] = useState<string | null>(null)
  const [gemeldet, setGemeldet] = useState(false)

  const geheim = eintrag.kind === 'secret'
  const nurLesen = eintrag.level === 'readonly' || eintrag.source === 'env'
  const feldWert = entwurf ?? (geheim ? '' : (eintrag.value ?? ''))
  const geaendert = entwurf !== null && entwurf !== (geheim ? '' : (eintrag.value ?? ''))
  const beschaeftigt = save.isPending

  async function speichern(wert: string | null) {
    if (eintrag.level === 'checked' && !(await rueckfrage(t.geprueftFrage(eintrag.key), t.aendern))) {
      return
    }
    setGemeldet(false)
    save.mutate(
      { key: eintrag.key, value: wert },
      {
        onSuccess: () => {
          setEntwurf(null)
          setGemeldet(true)
        },
      },
    )
  }

  const quelle =
    eintrag.source === 'env'
      ? t.ausUmgebung
      : eintrag.source === 'file'
        ? t.quelleDatei
        : t.quelleVorgabe

  return (
    <div className={'envzeile' + (eintrag.level === 'checked' ? ' envzeile--geprueft' : '')} data-env-key={eintrag.key} data-env-level={eintrag.level}>
      <div className="envzeile__kopf">
        <code className="envzeile__name">{eintrag.key}</code>
        <span className="envzeile__quelle">{quelle}</span>
      </div>
      <p className="envzeile__satz">{eintrag.hint}</p>
      {eintrag.level === 'checked' && !nurLesen && <p className="envzeile__warnung">{t.geprueft}</p>}
      {eintrag.ceiling_for !== undefined && (
        <p className="envzeile__satz">{t.decke(eintrag.ceiling, eintrag.chosen)}</p>
      )}

      {nurLesen ? (
        <p className="envzeile__wert" data-env-wert="">
          {geheim ? (eintrag.set ? '••••••••' : t.leer) : (eintrag.value === null || eintrag.value === '' ? t.leer : eintrag.value)}
          {eintrag.level === 'readonly' && <span className="envzeile__quelle"> · {t.nurFtp}</span>}
        </p>
      ) : (
        <div className="envzeile__feld">
          {geheim && (
            <span className="envzeile__quelle" data-env-geheim={eintrag.set ? 'gesetzt' : 'leer'}>
              {eintrag.set ? t.gesetzt : t.leer}
            </span>
          )}
          {eintrag.kind === 'bool' ? (
            <select
              className="field__input"
              value={feldWert}
              disabled={!schreibbar || beschaeftigt}
              onChange={(event) => setEntwurf(event.target.value)}
              aria-label={eintrag.key}
            >
              <option value="true">true</option>
              <option value="false">false</option>
            </select>
          ) : eintrag.kind === 'enum' ? (
            <select
              className="field__input"
              value={feldWert}
              disabled={!schreibbar || beschaeftigt}
              onChange={(event) => setEntwurf(event.target.value)}
              aria-label={eintrag.key}
            >
              {(eintrag.options ?? []).map((wahl) => (
                <option key={wahl} value={wahl}>
                  {wahl}
                </option>
              ))}
            </select>
          ) : (
            <input
              className="field__input"
              type={eintrag.kind === 'int' ? 'number' : 'text'}
              inputMode={eintrag.kind === 'int' ? 'numeric' : undefined}
              min={eintrag.min}
              max={eintrag.max}
              value={feldWert}
              placeholder={geheim ? t.neuerWert : (eintrag.default ?? '')}
              disabled={!schreibbar || beschaeftigt}
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck={false}
              onChange={(event) => setEntwurf(event.target.value)}
              aria-label={eintrag.key}
            />
          )}
          <button
            type="button"
            className="button button--small"
            disabled={!schreibbar || beschaeftigt || !geaendert}
            onClick={() => void speichern(feldWert)}
            data-env-speichern=""
          >
            {t.speichern}
          </button>
          {eintrag.optional && eintrag.source === 'file' && (
            <button
              type="button"
              className="button button--small button--outline"
              disabled={!schreibbar || beschaeftigt}
              onClick={() => void speichern(null)}
              data-env-aus=""
            >
              {t.auskommentieren}
            </button>
          )}
        </div>
      )}

      {!nurLesen && eintrag.default !== null && eintrag.default !== '' && eintrag.source !== 'default' && (
        <span className="envzeile__quelle">{t.vorgabe(eintrag.default)}</span>
      )}
      {save.isError && (
        <p className="notice" role="alert" data-env-fehler="">
          {fehlertext(save.error)}
        </p>
      )}
      {gemeldet && !save.isError && (
        <p className="feld__hinweis feld__hinweis--13" role="status" data-env-gespeichert="">
          {t.gespeichert}
        </p>
      )}
    </div>
  )
}
